import type { FetchOptions } from "ofetch";

export const promiseWithTimeout = async <T = any>(
  promise: Promise<any>,
  timeoutMs = 15000,
) => {
  let timeout: NodeJS.Timeout | undefined;

  const fetchPromise = promise;
  const timeoutPromise = new Promise<undefined>((_, reject) => {
    timeout = setTimeout(() => {
      reject({
        status: 408,
        message: `Timeout fetching in ${timeoutMs}ms`,
      });
    }, timeoutMs);
  });

  // Run a promise race. If storyblok doesn't respond within the timeout, we throw an error
  type PromiseRaceResult = Awaited<T> | undefined;
  let promiceRaceResponse: PromiseRaceResult;

  try {
    promiceRaceResponse = await Promise.race<PromiseRaceResult>([
      fetchPromise,
      timeoutPromise,
    ]);
  } finally {
    clearTimeout(timeout);
  }

  // Typecast to success response.
  // If the timeout promise resolved first, we would have already thrown an error.
  return promiceRaceResponse as Awaited<T>;
};

type FetchWithTimeoutOptions = FetchOptions & { timeout?: number };
export const $fetchWithTimeout = async <T = any>(
  url: string,
  options: FetchWithTimeoutOptions = {},
) => {
  const { timeout, ...fetchOptions } = options;
  const fetchPromise = $fetch<T>(url, fetchOptions);

  return await promiseWithTimeout<T>(fetchPromise, timeout);
};
